// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused".
// The hub import warmed the Rematch model cluster; this specifier restores
// that init order. It retires with the Z2 codemod.
import '@/pages/api/scribeAPIV2';

import { menuCoachmarkStepAtom } from '@/atoms/menuCoachmarkAtoms';
import { sidebarPanelPinnedAtom } from '@/atoms/sidebarPanelAtoms';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightProgress,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useMutateMarkModalAsViewed } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { sharedMessages } from './coachmark.messages';
import { PANEL_EXPAND_SETTLE_MS, TOTAL_STEPS } from './constants';

const messages = defineMessages({
  title: {
    defaultMessage: 'Start a new session anytime',
    id: 'yWoG6F0122',
    description: 'Coachmark title for the global Create entry point',
  },
  body: {
    defaultMessage: 'Use Create to start a new session from anywhere in Heidi.',
    id: 'KPy6cjMyRs',
    description: 'Coachmark body for the global Create entry point',
  },
});

interface MenuCoachmarkCreateStepProps {
  children?: ReactNode;
}

/**
 * Step 3 of the menu coachmark tour (conditional on global create flag).
 * Self-contained: reads atom state directly, provides its own handlers.
 * Shows the global Create button for starting new sessions.
 *
 * Figma: node 1519:81267
 */
export const MenuCoachmarkCreateStep = ({ children }: MenuCoachmarkCreateStepProps) => {
  const currentStep = useAtomValue(menuCoachmarkStepAtom);
  const setStep = useSetAtom(menuCoachmarkStepAtom);
  const setIsPinned = useSetAtom(sidebarPanelPinnedAtom);
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();
  const transitionTimerRef = useRef<number | null>(null);

  const isOpen = currentStep === 3;

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        window.clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  const handleDismiss = useCallback(() => {
    markModalAsViewed({
      modal_id: ModalId.MENU_COACHMARK_TOUR,
      increment_count: true,
    });
  }, [markModalAsViewed]);

  const handleNext = useCallback(() => {
    setIsPinned(true);
    transitionTimerRef.current = window.setTimeout(() => {
      setStep(4);
    }, PANEL_EXPAND_SETTLE_MS);
  }, [setStep, setIsPinned]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setStep(null);
      }
    },
    [setStep]
  );

  if (!isOpen) return <>{children}</>;

  return (
    <Spotlight open={isOpen} onOpenChange={handleOpenChange}>
      {children && <SpotlightAnchor>{children}</SpotlightAnchor>}
      <SpotlightContent
        align="start"
        alignOffset={0}
        side="right"
        sideOffset={12}
        data-testid="menu-coachmark-step-3"
        onPointerDownOutside={(e) => {
          e.preventDefault();
          handleNext();
        }}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader>
          <SpotlightTitle>
            <FormattedMessage {...messages.title} />
          </SpotlightTitle>
          <SpotlightClose
            onClick={handleDismiss}
            data-testid="menu-coachmark-step-3-close"
          />
        </SpotlightHeader>
        <SpotlightDescription>
          <FormattedMessage {...messages.body} />
        </SpotlightDescription>
        <SpotlightFooter>
          <SpotlightProgress>
            <FormattedMessage
              {...sharedMessages.stepProgress}
              values={{ current: 3, total: TOTAL_STEPS }}
            />
          </SpotlightProgress>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleNext}
            className="bg-surface text-text-primary hover:bg-background-secondary"
            data-testid="menu-coachmark-step-3-next"
          >
            <FormattedMessage {...sharedMessages.next} />
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
};
